import { useQuery } from "@tanstack/react-query";
import { employeeProfileKeys, roleClassificationKeys } from "@/lib/query";

/** Mirrors `NosLinkStatus` in `@/api/nos-link-check`. */
export type NosLinkStatus = "matched" | "name_differs" | "missing_in_nos" | "not_linked";

export interface NosLinkRow {
  id: string;
  name: string;
  nosId: string | null;
  nosName: string | null;
  status: NosLinkStatus;
}

export interface NosLinkCheck {
  configured: boolean;
  reachable: boolean;
  error?: string;
  rows: NosLinkRow[];
  unmatchedInNos: { id: string; name: string }[];
}

/**
 * The NOS side of a config page, fetched alongside the list rather than as part
 * of it: the page renders from the database immediately and the NOS column
 * fills in when the gateway answers. That keeps a slow or unreachable gateway
 * from holding up a page that is perfectly usable without it.
 *
 * `retry: false` on purpose — the two failure modes (no gateway credentials,
 * gateway down) are both reported as data, and neither is fixed by retrying.
 */
function useNosCheck(key: readonly unknown[], url: string) {
  return useQuery({
    queryKey: [...key, "nos-check"],
    queryFn: async (): Promise<NosLinkCheck> => {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to check NOS links");
      const json = await res.json();
      return json.data;
    },
    retry: false,
    // A live read through the gateway to NOS — worth a minute of cache rather
    // than one round trip per render.
    staleTime: 60_000,
  });
}

export function useEmployeeProfileNosCheck() {
  return useNosCheck(employeeProfileKeys.all, "/api/employee-profiles/nos-check");
}

export function useRoleClassificationNosCheck() {
  return useNosCheck(roleClassificationKeys.all, "/api/role-classifications/nos-check");
}

/** One row's check result, or undefined while the check is still in flight. */
export function nosRowFor(check: NosLinkCheck | undefined, id: string): NosLinkRow | undefined {
  return check?.rows.find((r) => r.id === id);
}
